import React from 'react';
import './App.css';
import { 
  Zap, 
  Target, 
  Rocket, 
  Users, 
  CheckCircle2, 
  ArrowRight, 
  Clock, 
  DollarSign,
  MessageSquare,
  Code2,
  Headphones,
  TrendingUp,
  Sparkles,
  Shield,
  Calendar
} from 'lucide-react';

function App() {
  const handleApplyClick = () => {
    window.open('https://docs.google.com/forms/d/e/1FAIpQLSfSIcL24Rdfh_6rrcDwO4_kE-DQXzSXJBn3ZwQZ1XbOS8Rz-w/viewform', '_blank');
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <div className="logo">
            <img src="/icon.png" alt="SnapLaunch" className="logo-icon-img" />
            <span>SnapLaunch</span>
          </div>
          <button className="nav-cta" onClick={handleApplyClick}>
            Apply Now
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>For Creators & Influencers</span>
          </div>
          <h1>
            Build a Product With Your Audience
            <span className="gradient-text"> — In 21 Days</span>
          </h1>
          <p className="hero-subtitle">
            We turn your audience's biggest needs into a real, revenue-generating product.
            <strong> No upfront cost. No long commitments.</strong>
          </p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={handleApplyClick}>
              Start Your Product Sprint
              <ArrowRight size={20} />
            </button>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-value">21</span>
                <span className="stat-label">Days to Launch</span>
              </div>
              <div className="stat">
                <span className="stat-value">$0</span>
                <span className="stat-label">Upfront Cost</span>
              </div>
              <div className="stat">
                <span className="stat-value">50%</span>
                <span className="stat-label">Revenue Share</span>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="floating-card card-1">
            <MessageSquare size={24} />
            <span>Your audience has real needs</span>
          </div>
          <div className="floating-card card-2">
            <Rocket size={24} />
            <span>Turn into a product</span>
          </div>
          <div className="floating-card card-3">
            <DollarSign size={24} />
            <span>Generate revenue</span>
          </div>
        </div>
      </section>

      {/* What This Is Section */}
      <section className="what-section">
        <div className="container">
          <h2>What This Is</h2>
          <div className="what-content">
            <p className="lead">
              A <strong>2–3 week product sprint</strong> where we:
            </p>
            <div className="what-grid">
              <div className="what-item">
                <Target className="what-icon" />
                <span>Identify a <em>real pain</em> your audience already has</span>
              </div>
              <div className="what-item">
                <Code2 className="what-icon" />
                <span>Build a small, focused product around it</span>
              </div>
              <div className="what-item">
                <Rocket className="what-icon" />
                <span>Launch it to your audience</span>
              </div>
              <div className="what-item">
                <DollarSign className="what-icon" />
                <span>Share the revenue</span>
              </div>
            </div>
            <div className="what-disclaimer">
              <p>This is <strong>not</strong> a brainstorming partnership.</p>
              <p>It's a fast execution program with a clear outcome.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="who-section">
        <div className="container">
          <h2>Who This Is For</h2>
          <p className="section-subtitle">You're a good fit if:</p>
          <div className="fit-grid">
            <div className="fit-card">
              <CheckCircle2 className="fit-icon" />
              <p>You have an engaged niche audience <span>(not just views)</span></p>
            </div>
            <div className="fit-card">
              <CheckCircle2 className="fit-icon" />
              <p>You see your audience <em>struggling with the same problems</em></p>
            </div>
            <div className="fit-card">
              <CheckCircle2 className="fit-icon" />
              <p>You already explain or share workarounds</p>
            </div>
            <div className="fit-card">
              <CheckCircle2 className="fit-icon" />
              <p>You want to monetize without selling courses or doing ads</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="process-section">
        <div className="container">
          <h2>How It Works</h2>
          <p className="section-subtitle">End-to-End Process</p>
          
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker">1</div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <h3>Intro Call</h3>
                  <span className="duration">30 min</span>
                </div>
                <ul>
                  <li>Align on goals</li>
                  <li>Validate audience pain</li>
                  <li>Decide if there's a product worth building</li>
                </ul>
                <div className="outcome">
                  <strong>Outcome:</strong> Go / No-go decision
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">2</div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <h3>Ideation Session</h3>
                  <span className="duration">45 min</span>
                </div>
                <ul>
                  <li>Define <strong>one</strong> clear problem</li>
                  <li>Shape a simple, opinionated solution</li>
                  <li>Lock MVP scope (what it does / doesn't do)</li>
                  <li>Define the marketing plan</li>
                </ul>
                <div className="outcome">
                  <strong>Outcome:</strong> One-page product brief + marketing plan
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">3</div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <h3>Build Sprint</h3>
                  <span className="duration">7–10 days</span>
                </div>
                <ul>
                  <li>We build the MVP</li>
                  <li>You approve name & copy</li>
                  <li>Async updates only (no meetings)</li>
                </ul>
                <div className="outcome">
                  <strong>Outcome:</strong> Working product + landing page
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">4</div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <h3>Private Beta</h3>
                  <span className="duration">3–5 days</span>
                </div>
                <ul>
                  <li>Launch to a small trusted group</li>
                  <li>Validate real usage & willingness to pay</li>
                </ul>
                <div className="outcome">
                  <strong>Outcome:</strong> Decide to launch, tweak, or stop
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">5</div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <h3>Public Launch</h3>
                  <span className="duration">Launch day</span>
                </div>
                <ul>
                  <li>You share with your audience</li>
                  <li>We handle tech, payments, support</li>
                </ul>
                <div className="outcome">
                  <strong>Outcome:</strong> Revenue + feedback + momentum
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Responsibilities Section */}
      <section className="responsibilities-section">
        <div className="container">
          <div className="resp-grid">
            <div className="resp-card we-handle">
              <h3>What We Handle</h3>
              <ul>
                <li><Code2 size={18} /> Product design & development</li>
                <li><Sparkles size={18} /> AI / automation logic</li>
                <li><TrendingUp size={18} /> Hosting, payments, analytics</li>
                <li><Headphones size={18} /> Customer support</li>
                <li><Rocket size={18} /> Iteration & scaling</li>
              </ul>
            </div>
            <div className="resp-card you-do">
              <h3>What You Do</h3>
              <ul>
                <li><Users size={18} /> Share audience insight</li>
                <li><Target size={18} /> Validate direction</li>
                <li><MessageSquare size={18} /> Promote the product</li>
              </ul>
              <p className="thats-it">That's it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Section */}
      <section className="revenue-section">
        <div className="container">
          <h2>Revenue & Ownership</h2>
          <div className="revenue-grid">
            <div className="revenue-card">
              <DollarSign className="revenue-icon" />
              <h4>Revenue Share</h4>
              <p className="revenue-value">30–50%</p>
              <p className="revenue-note">Based on involvement</p>
            </div>
            <div className="revenue-card">
              <Shield className="revenue-icon" />
              <h4>No Upfront Costs</h4>
              <p className="revenue-value">$0</p>
              <p className="revenue-note">We take the risk</p>
            </div>
            <div className="revenue-card">
              <Calendar className="revenue-icon" />
              <h4>No Lock-in</h4>
              <p className="revenue-value">Clean Exit</p>
              <p className="revenue-note">If it doesn't work</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-summary">
        <div className="container">
          <div className="timeline-box">
            <Clock className="timeline-icon" />
            <div className="timeline-text">
              <h3>Total Time: 14–21 Days</h3>
              <p>If there's no traction — we stop and move on.</p>
              <p className="no-zombie">No zombie projects.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Works Section */}
      <section className="why-section">
        <div className="container">
          <h2>Why This Works</h2>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-number">01</div>
              <p>Product is built <em>from real audience pain</em></p>
            </div>
            <div className="why-card">
              <div className="why-number">02</div>
              <p>Distribution is solved on day one</p>
            </div>
            <div className="why-card">
              <div className="why-number">03</div>
              <p>Small scope → fast results</p>
            </div>
            <div className="why-card">
              <div className="why-number">04</div>
              <p>Low risk, high upside</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <h2>Ready to Build Something Real?</h2>
            <p>Fill a short form (5 minutes).</p>
            <p>If there's a strong fit, we'll schedule the intro call.</p>
            <button className="btn-primary large" onClick={handleApplyClick}>
              Apply Now — It's Free
              <ArrowRight size={24} />
            </button>
            <p className="cta-note">No commitment. Just a conversation.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <Zap className="logo-icon" />
              <span>SnapLaunch</span>
            </div>
            <p>© 2026 SnapLaunch by Quantifyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
